import { BsPlusCircle } from "react-icons/bs";
import Sidebar from "../../components/Sidebar/Sidebar";
import { Topbar } from "../../components/Topbar/Topbar";
import "./CreateAssignmentPage.scss";
import Input from "../../components/Input/Input";
import { Class, Module } from "../../types";
import { useState, useEffect, ReactElement } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosClient from "../../axiosClient";
import { useUser } from "../../context/UserContext";
import ModuleSelector from "./ModuleSelector/ModuleSelector";
import BootstrapButton from "react-bootstrap/Button";
import { ImCheckmark, ImCross } from "react-icons/im";
import toast from "react-hot-toast";

const CreateAssignmentPage = () => {
  const { user } = useUser();
  const teacherId = user?.teacher?.id;
  const [classes, setClasses] = useState<Class[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [moduleSelectors, setModuleSelectors] = useState<ReactElement[]>([]);
  const [selectedModules, setSelectedModules] = useState<{
    [moduleId: number]: number;
  }>({});

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Students: timed practice is subject-wide only (ModulesListPage → TestExamModal).
  // Block the legacy per-module create-assignment flow.
  useEffect(() => {
    if (!user || user.type === "teacher") {
      return;
    }
    const subjectId = searchParams.get("subjectId");
    toast.error(
      "Timed practice is only available for a whole subject. Use Timed Practice on the subject page.",
      { duration: 4000 },
    );
    navigate(subjectId ? `/subjects/${subjectId}` : "/dashboard", {
      replace: true,
    });
  }, [user, searchParams, navigate]);

  useEffect(() => {
    if (user?.type !== "teacher") {
      return;
    }

    const getClasses = async () => {
      const classes: Class[] = (
        await axiosClient.get(`/teachers/${teacherId}/classes`)
      ).data;
      setClasses(classes);
      const modules: Module[] = (
        await axiosClient.get(`/subjects/${classes[0].subjectId}/modules`)
      ).data.modules;
      setModules(modules);
      setIsLoading(false);
    };

    try {
      getClasses();
    } catch (err) {
      toast.error("Error loading page. Please seek support");
      console.error(err);
      setIsLoading(false);
    }
  }, [user, teacherId]);

  useEffect(() => {
    console.log(selectedModules);
  }, [selectedModules]);

  const onSelectModuleQuestions = (
    module: Module,
    numberOfQuestions: number,
  ) => {
    setSelectedModules({
      ...selectedModules,
      [module.id]: numberOfQuestions,
    });
  };

  const classOptions = classes.map((c) => {
    return {
      value: c.classId,
      name: c.className,
    };
  });

  const timeAllowedOptions = [
    {
      value: 5,
      name: "5 minutes",
    },
    {
      value: 10,
      name: "10 minutes",
    },
    {
      value: 15,
      name: "15 minutes",
    },
    {
      value: 30,
      name: "30 minutes",
    },
    {
      value: 45,
      name: "45 minutes",
    },
    {
      value: 60,
      name: "1 hour",
    },
    {
      value: 75,
      name: "1 hour and 15 minutes",
    },
    {
      value: 90,
      name: "1 hour and 30 minutes",
    },
    {
      value: 105,
      name: "1 hour and 45 minutes",
    },
    {
      value: 120,
      name: "2 hours",
    },
  ];

  const onClassChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    try {
      const classId = Number(e.target.value);
      const subjectId = classes.filter((c) => c.classId === classId)[0]
        .subjectId;
      const modules: Module[] = (
        await axiosClient.get(`/subjects/${subjectId}/modules`)
      ).data;
      setModules(modules);

      // reset/clear all values
      document
        .querySelectorAll(".numberOfQuestionsSelector")
        .forEach((inputEl) => {
          (inputEl as HTMLInputElement).value = "0";
        });
    } catch (err) {
      toast.error("Error occured whilst loading modules. Please seek support");
      console.error(err);
    }
  };

  if (user && user.type !== "teacher") {
    return null;
  }

  return (
    <div className="page-wrapper" id="CreateAssignmentPage">
      <Sidebar />
      <div className="internal-page-wrapper">
        <Topbar
          title={user?.type === "teacher" ? "Create Assignment" : "Timed Practice"}
        />
        <main>
          {isLoading ? (
            <p>Loading....</p>
          ) : (
            <form
              action=""
              onSubmit={async (e: any) => {
                e.preventDefault();
                // Class assignment creation is no longer supported via this endpoint.
                // Timed practice must be subject-wide (module-all) from the subject page.
                toast.error(
                  "Creating assignments from selected modules is no longer supported. Use Timed Practice on the subject page for subject-wide exams.",
                );
              }}
            >
              <div className="white-box">
                <div className="exam-details">
                  {user?.type === "teacher" && (
                    <>
                      <Input
                        label="Name"
                        name="name"
                        type="text"
                        placeholder="Assignment name"
                      />
                      <Input
                        label="Exam due date"
                        name="examDueDate"
                        type="date"
                      />

                      <Input
                        label="Class"
                        type="select"
                        name="class"
                        options={classOptions}
                        onChange={onClassChange}
                      />
                    </>
                  )}

                  <Input
                    label="Time allowed"
                    type="select"
                    name="timeAllowed"
                    options={timeAllowedOptions}
                  />

                  {user?.type === "teacher" && (
                    <Input
                      label="Pass mark (%)"
                      name="passMark"
                      type="number"
                      placeholder="Pass mark (%)"
                    />
                  )}
                </div>

                <div className="topicQuestionAllocation">
                  <div className="titles">
                    <div>Modules</div>
                    <div>Number of questions to be tested on</div>
                  </div>
                  <div className="allocations" id="moduleAllocations">
                    {moduleSelectors}
                  </div>
                </div>

                <BootstrapButton
                  onClick={(e) => {
                    e.preventDefault();
                    setModuleSelectors([
                      ...moduleSelectors,
                      <ModuleSelector
                        modules={modules}
                        onSelectModuleQuestions={onSelectModuleQuestions}
                      />,
                    ]);
                  }}
                  variant="primary"
                >
                  <BsPlusCircle
                    style={{
                      marginRight: "5px",
                    }}
                  />
                  Add module
                </BootstrapButton>

                <div className="buttons">
                  <BootstrapButton
                    variant="danger"
                    onClick={() => {
                      navigate("/dashboard");
                    }}
                  >
                    <ImCross
                      color="red"
                      size={18}
                      style={{
                        backgroundColor: "white",
                        borderRadius: "10px",
                        padding: "5px",
                        marginRight: "5px",
                      }}
                    />
                    Cancel
                  </BootstrapButton>

                  <BootstrapButton
                    variant="success"
                    type="submit"
                    style={{
                      marginLeft: "10px",
                    }}
                  >
                    {" "}
                    <ImCheckmark
                      color="green"
                      size={18}
                      style={{
                        backgroundColor: "white",
                        borderRadius: "10px",
                        padding: "5px",
                        marginRight: "5px",
                      }}
                    />{" "}
                    Create
                  </BootstrapButton>
                </div>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default CreateAssignmentPage;
